/**
 * SafetyQueue — open interpersonal-safety events across administrations, with an
 * acknowledge action. Mirrors the behavioral-assessment SafetyEventsQueue.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, Loader2, RefreshCw, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { socialNeedsScreenerApi, type SafetyEvent } from './types';

export const SafetyQueue: React.FC = () => {
  const [events, setEvents] = useState<SafetyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    socialNeedsScreenerApi
      .listSafetyEvents('open')
      .then((r) => setEvents(r.safety_events))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  const acknowledge = useCallback(
    async (id: string) => {
      setAcking(id);
      try {
        await socialNeedsScreenerApi.acknowledgeSafetyEvent(id);
        setEvents((prev) => prev.filter((e) => e.id !== id));
      } catch {
        // leave the event in place; the user can retry
      } finally {
        setAcking(null);
      }
    },
    [],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-red-600" aria-hidden="true" />
            Open safety events
          </span>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading…
          </div>
        ) : events.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No open safety events.</p>
        ) : (
          events.map((e) => (
            <div
              key={e.id}
              className="flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-3"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 font-medium">
                  {e.first_name || e.last_name
                    ? `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim()
                    : 'Member'}
                  <Badge variant="destructive" className="text-[10px]">
                    {e.severity}
                  </Badge>
                </div>
                <div className="text-sm text-muted-foreground">{e.detail}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {new Date(e.triggered_at).toLocaleString()}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void acknowledge(e.id)}
                disabled={acking === e.id}
              >
                {acking === e.id ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Check className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                )}
                Acknowledge
              </Button>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};
